import { useEffect, useMemo, useRef, useState } from "react";
import type { WorkObject } from "../../../packages/appdock-core/src/types";

interface UseWorkObjectsOptions {
  filters?: Record<string, any>;
  enableSSE?: boolean;
}

export function useWorkObjects(options: UseWorkObjectsOptions = {}) {
  const { filters = {}, enableSSE = true } = options;
  const [data, setData] = useState<{items: WorkObject[], nextCursor?: string}>({items:[]});
  const [loading, setLoading] = useState(false);
  const esRef = useRef<EventSource | null>(null);

  const qs = useMemo(() => {
    const u = new URLSearchParams();
    Object.entries(filters).forEach(([k,v]) => {
      if (v === undefined || v === null) return;
      if (Array.isArray(v)) v.forEach(x => u.append(k, String(x)));
      else u.set(k, String(v));
    });
    return u.toString();
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/workobjects?${qs}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      }
    })
    .then(r => r.json())
    .then(d => {
      if (!cancelled) setData(d);
    })
    .catch(err => console.error('Failed to fetch work objects:', err))
    .finally(() => !cancelled && setLoading(false));
    
    return () => { cancelled = true; };
  }, [qs]);

  useEffect(() => {
    if (!enableSSE) return;
    
    esRef.current?.close();
    const es = new EventSource(`/api/workobjects/stream`);
    
    es.addEventListener('workobject.updated', (e: MessageEvent) => {
      try {
        const { obj } = JSON.parse(e.data);
        setData(curr => {
          const idx = curr.items.findIndex((x: WorkObject) => x.id === obj.id);
          const items = [...curr.items];
          if (idx >= 0) items[idx] = obj; else items.unshift(obj);
          return { ...curr, items };
        });
      } catch (err) {
        console.error('Failed to parse SSE event:', err);
      }
    });
    
    esRef.current = es;
    return () => es.close();
  }, [enableSSE]);

  const createWorkObject = async (input: Partial<WorkObject>) => {
    const response = await fetch('/api/workobjects', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(input)
    });
    return response.json();
  };

  const updateWorkObject = async (id: string, updates: Partial<WorkObject>) => {
    const response = await fetch(`/api/workobjects/${id}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    });
    return response.json();
  };

  return { data, loading, createWorkObject, updateWorkObject };
};
